import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Ng4LoadingSpinnerService } from 'ng4-loading-spinner';
import { BsModalRef, BsModalService } from 'ngx-bootstrap';
import { Subject, of } from 'rxjs';
import { catchError, finalize, takeUntil, tap } from 'rxjs/operators';
import { PayoutListResponse, PayoutListToConfirm } from 'src/app/_models/massive-payout-confirmation-process';
import { PayoutProcessCommerceMasiveConfirmService } from 'src/app/_services/payout-process-commerce-masive-confirm.service';
import { payoutMassiveConfirmStatusMap } from 'src/app/sharedcomp/constans/payoutMassiveConfirm.constans';

@Component({
  selector: 'app-payout-process-commerce-masive-confirm',
  templateUrl: './payout-process-commerce-masive-confirm.component.html',
  styleUrls: ['./payout-process-commerce-masive-confirm.component.css'],
  providers: [Ng4LoadingSpinnerService]
})
export class PayoutProcessCommerceMasiveConfirmComponent implements OnInit, OnDestroy {

  bankGroupDataArr: { name: string; public_id: string; }[];
  confirmForm: FormArray;
  isLoadingActive: boolean = false;
  isConfirmButtonActive: boolean = false;
  private mainBanks = ['md01', 'md02', 'md03', 'md04'];
  timerId: NodeJS.Timeout;
  payoutRelation: { public_id: string; banks: any[]; }[] = [];
  @ViewChild("modalConfirmationMessage") modalConfirmationMessage;
  modalRef: BsModalRef;

  customerPublicIds: string;
  payoutPublicIds: string;

  payoutsCounter: number;
  isConfirmSuccess: boolean = false;
  confirmMessage: string;
  private destroy$ = new Subject<void>();

  constructor(
    private _fb: FormBuilder,
    private payoutMasiveConfirmService: PayoutProcessCommerceMasiveConfirmService,
    private spinnerService: Ng4LoadingSpinnerService,
    private modalService: BsModalService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.initialSetup();
  }

  ngOnInit() {
    this.getPayoutList();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getPayoutList() {
    this.spinnerService.show();
    this.customerPublicIds = this.route.snapshot.queryParamMap.get('customer_public_ids');
    this.payoutPublicIds = this.route.snapshot.queryParamMap.get('payout_public_ids');

    if (this.customerPublicIds && this.payoutPublicIds) {
      this.payoutMasiveConfirmService.getPayoutsToConfirmList(this.customerPublicIds, this.payoutPublicIds)
        .pipe(
          takeUntil(this.destroy$),
          tap((payoutList => {
            this.payoutsCounter = payoutList.length;
            this.buildCustomConfirmForm(payoutList);
            this.spinnerService.hide();
          })),
          catchError(error => {
            this.spinnerService.hide();
            console.error('Error! While trying to get the payout list', error)
            return of(null);
          })
        ).subscribe();
    } else {
      console.error('Error! While trying to get query params from the route');
      this.spinnerService.hide();
    }
  }

  openModal(template: TemplateRef<any>) {
    this.modalRef = this.modalService.show(template, { class: 'modal-lg modal-dialog-centered' });
  }

  hideModal(): void {
    this.modalRef.hide();
    this.router.navigate(['/payout_process_commerce/list']);
  }

  transformPayload(confirmForm: FormArray): PayoutListToConfirm[] {
    const bankData: { [key: string]: { pspValue: string; psp: string; filelist: { name: string; url: string }[] } } = {};
    let payoutData: PayoutListToConfirm[] = [];
    const formValue = confirmForm.value;

    formValue.forEach((bank: { psp?: any; pspValue?: any; url?: any; label?: any; name?: any; }) => {
      const { psp, pspValue, url, label } = bank;
      let { name } = bank;

      if (label != undefined && label.trim().length > 0) {
        name = label;
      }

      if (!bankData[psp]) {
        bankData[psp] = {
          pspValue: pspValue,
          psp: psp,
          filelist: [{ name, url }],
        };
      } else {
        bankData[psp].pspValue = pspValue;
        bankData[psp].filelist.push({ name, url });
      }
    });

    this.payoutRelation.forEach(payout => {
      let files = [];
      let op = {};

      payout.banks.forEach(bank => {
        bankData[bank].filelist.forEach(fileItem => {
          files.push(fileItem);
        });
        op[bank] = bankData[bank].pspValue;
      });

      payoutData.push({
        public_id: payout.public_id,
        file_list: files,
        operations: op
      })
    });
    return payoutData;
  }

  setPayoutDescription(description: string = ""): string {
    switch (description) {
      case 'SUCCESS':
        description = payoutMassiveConfirmStatusMap.SUCCESS;
        break;
      default:
        description = payoutMassiveConfirmStatusMap.ERROR;
        break;
    }
    return description;
  }

  confirm(confirmForm: FormArray) {

    try {
      this.spinnerService.show();
      const payoutData = this.transformPayload(confirmForm);
      this.payoutMasiveConfirmService.confirmPayoutList(payoutData)
        .pipe(
          tap(response => {
            if (response.status === 200) {
              this.isConfirmSuccess = true;
              this.confirmMessage = this.setPayoutDescription('SUCCESS');
              this.openModal(this.modalConfirmationMessage);
            } else {
              this.confirmMessage = this.setPayoutDescription();
            }
          }),
          catchError(error => {
            this.confirmMessage = this.setPayoutDescription();
            this.openModal(this.modalConfirmationMessage);
            console.error('Error! while trying to confirm massive payout', error);
            return of(null);
          }),
          finalize(() => {
            this.isConfirmButtonActive = false;
            this.spinnerService.hide();
          })
        )
        .subscribe();

    } catch (error) {
      this.spinnerService.hide();
      console.error('Error! while trying to confirm massive payout', error);
    }
  }

  initialSetup() {
    this.confirmForm = this._fb.array([], [Validators.minLength(1)]);
  }

  uniqueNameValidator(formArray: FormArray): { [key: string]: boolean } | null {
    const nameSet = new Set<string>();
    const duplicateNames = new Set<string>();

    for (let control of formArray.controls) {
      const pspValueControl = control.get('pspValue');
      const pspValue = pspValueControl.value;

      if (nameSet.has(pspValue)) {
        duplicateNames.add(pspValue);
      } else {
        nameSet.add(pspValue);
      }
    }

    let hasDuplicates = false;
    for (let control of formArray.controls) {
      const pspValueControl = control.get('pspValue');
      const pspValue = pspValueControl.value;

      if (duplicateNames.has(pspValue) && pspValue !== '') {
        const currentErrors = control.errors || {};
        pspValueControl.setErrors({ ...currentErrors, duplicateName: true });
        hasDuplicates = true;
      } else {
        if (pspValueControl.errors && pspValueControl.hasError('duplicateName')) {
          const { duplicateName, ...errors } = pspValueControl.errors;
          const currentErrors = Object.keys(errors).length === 0 ? null : errors;
          pspValueControl.setErrors(currentErrors);
        }
      }
    }

    return hasDuplicates ? { 'duplicateName': true } : null;
  }

  numberOnly(event): boolean {
    const charCode = (event.which) ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      if (!((charCode >= 37 && charCode <= 42) || charCode == 45 || charCode == 46 || charCode == 35 || charCode == 32)) {
        return false;
      }
    }
    return true;
  }

  notExistPreviousValue(arr, value): boolean {
    return !Object.values(arr).some(obj => obj["public_id"] === value);
  }

  isNotUndefinedOrNull(value, type) {
    return value !== type;
  }

  makeBankArr(arr: PayoutListResponse[]): { name: string, public_id: string }[] {
    const result: { name: string, public_id: string }[] = [];

    arr.forEach(obj => {
      const payoutInformation = { public_id: obj.public_id, banks: [] }
      obj.bank.forEach(data => {
        payoutInformation.banks.push(data.public_id);
        const typeBank = data.public_id.slice(-4);
        result.push({
          name: this.mainBanks.includes(typeBank) ? data.name : 'Interbancario',
          public_id: data.public_id
        });
      });

      this.payoutRelation.push(payoutInformation);
    });
    return result;
  }

  filterUniqueObjectsByProperty(newArray): { name: string, public_id: string }[] {
    let bankGroupArr = [];
    for (const obj of newArray) {
      const value = obj.public_id;
      if (this.notExistPreviousValue(bankGroupArr, value) && (this.isNotUndefinedOrNull(obj.public_id, undefined) || this.isNotUndefinedOrNull(obj.name, undefined))) {
        bankGroupArr.push(obj);
      }
    }
    return bankGroupArr;
  }

  getBankGroupDataArr(payoutList: PayoutListResponse[]) {
    const newArray = this.makeBankArr(payoutList);
    const bankGroup = this.filterUniqueObjectsByProperty(newArray);
    return bankGroup;
  }

  deleteBankGroup(index: number) {
    this.confirmForm.removeAt(index);
  }

  addBankGroup(bank: { name: string; public_id: string }, update = false) {
    this.isLoadingActive = true;
    this.confirmForm.push(this._fb.group({
      name: new FormControl(bank.name, [Validators.required, Validators.minLength(1)]),
      label: new FormControl(''),
      url: new FormControl(''),
      pspValue: new FormControl('', [Validators.required]),
      psp: new FormControl(bank.public_id),
    }));

    if (update) {
      this.uniqueNameValidator(this.confirmForm);
    }
  }

  buildCustomConfirmForm(payoutList: PayoutListResponse[]) {
    this.bankGroupDataArr = this.getBankGroupDataArr(payoutList);
    this.bankGroupDataArr.forEach(banco => {
      this.addBankGroup(banco);
    });
    this.isConfirmButtonActive = true;
  }

  onOptionChange(event: Event, index: number) {
    const selectElement = event.target as HTMLSelectElement;
    const dataId = selectElement.options[selectElement.selectedIndex].getAttribute('data-id');
    const formGroupAtIndex = this.confirmForm.at(index) as FormGroup;
    formGroupAtIndex.get('psp').setValue(dataId);
  }

}
